import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import {
  AlertTriangle,
  ArrowRightLeft,
  ChevronLeft,
  ChevronRight,
  Shield,
  ShieldOff,
  MailPlus,
  Trash2,
  UserPlus,
  Users as UsersIcon,
  X,
} from "lucide-react";

import UserAvatar from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ADMIN_DISPLAY_VALUE,
  ADMIN_GROUP,
  ADMIN_ROLE_DESCRIPTION,
  CLIENT_OWNER_DISPLAY_VALUE,
  CLIENT_OWNER_GROUP,
  COGNITO_STATUS_INVITE_PENDING,
  EMPTY_STRING,
  PENTESTER_DISPLAY_VALUE,
  PENTESTER_GROUP,
  PENTESTER_ROLE_DESCRIPTION,
} from "@/constants/definitions";
import { WebAppOperationsClass } from "@/models/webAppOperations";
import CognitoLoginClass from "@/models/cognitoLogin";
import UserClass from "@/models/user";
import WebApplicationClass from "@/models/webApplication";
import AddUser from "@/components/users/AddUser";
import SortSelect from "@/components/common/SortSelect";
import HelpTip from "@/components/common/HelpTip";
import RolesLegend from "@/components/common/RolesLegend";
import { useTenantUsage } from "@/utils/useTenantUsage";
import { resendUserInvite, resendInviteMessage } from "@/services/userInvite.service";
import { useModalAccessibility } from "@/utils/useModalAccessibility";

// Shown on the disabled "Add User" button when the tenant is at its
// plan's seat cap. Kept as a named constant alongside the page's other
// user-facing copy.
const SEAT_LIMIT_REACHED_TOOLTIP = "You've reached your plan's user limit";

const USERS_PER_PAGE = 50;

// How long an action notice stays up before dismissing itself. Long enough
// to read a sentence naming apps or people, short enough not to linger.
const NOTICE_DISMISS_MS = 8000;

const CANT_DELETE_SELF_ERROR = "You cannot delete your own account";
const CANT_DELETE_CLIENT_OWNER_ERROR = "Cannot delete the Account Owner — the Account Owner must first downgrade their role";
const CANT_CHANGE_CLIENT_OWNER_ROLE_ERROR = "Cannot change the Account Owner's role";
const COULD_NOT_CHANGE_ROLE_ERROR = "Couldn't change the role. Please try again.";
const COULD_NOT_RESET_MFA_ERROR = "Couldn't reset two-factor authentication. Please try again.";
const CANT_TRANSFER_OWNERSHIP_TO_SELF_ERROR = "Cannot transfer account ownership to yourself";
const CANT_TRANSFER_APPS_TO_SELF_ERROR = "Cannot transfer apps to the same user";
const NO_APPS_TO_TRANSFER_ERROR = "This user has no apps to transfer";
const COULD_NOT_TRANSFER_OWNERSHIP_ERROR = "Couldn't transfer account ownership. Please try again.";
const COULD_NOT_TRANSFER_APPS_ERROR = "Couldn't transfer some apps. Please try again.";

// The roles this dialog can assign, least privileged first. Account Owner is
// deliberately absent: ownership moves only through the transfer action, which
// is Owner-only and has its own confirmation.
const ROLE_CHOICES = [
  {
    group: PENTESTER_GROUP,
    label: PENTESTER_DISPLAY_VALUE,
    description: PENTESTER_ROLE_DESCRIPTION,
    iconClassName: "text-gray-400",
  },
  {
    group: ADMIN_GROUP,
    label: ADMIN_DISPLAY_VALUE,
    description: ADMIN_ROLE_DESCRIPTION,
    iconClassName: "text-blue-400",
  },
] as const;

const ROLE_SORT_ORDER: Record<string, number> = {
  [CLIENT_OWNER_GROUP]: 0,
  [ADMIN_GROUP]: 1,
  [PENTESTER_GROUP]: 2,
};

function getRoleSortPriority(roleList: string[]): number {
  const roleString = roleList.join(",");
  if (roleString.includes(CLIENT_OWNER_GROUP)) return ROLE_SORT_ORDER[CLIENT_OWNER_GROUP];
  if (roleString.includes(ADMIN_GROUP)) return ROLE_SORT_ORDER[ADMIN_GROUP];
  if (roleString.includes(PENTESTER_GROUP)) return ROLE_SORT_ORDER[PENTESTER_GROUP];
  return 3;
}

function getRoleBadge(roleList: string[]): { label: string; className: string } {
  const roleString = roleList.join(",");
  if (roleString.includes(CLIENT_OWNER_GROUP)) {
    return { label: CLIENT_OWNER_DISPLAY_VALUE, className: "bg-emerald-50 text-emerald-700 ring-emerald-600/20" };
  }
  if (roleString.includes(ADMIN_GROUP)) {
    return { label: ADMIN_DISPLAY_VALUE, className: "bg-blue-50 text-blue-700 ring-blue-600/20" };
  }
  if (roleString.includes(PENTESTER_GROUP)) {
    return { label: PENTESTER_DISPLAY_VALUE, className: "bg-gray-50 text-gray-700 ring-gray-600/20" };
  }
  return { label: "User", className: "bg-gray-50 text-gray-600 ring-gray-500/20" };
}

// The user being managed and the current view shown in the Manage modal.
type ManageView =
  | "overview"
  | "changeRole"
  | "transferApps"
  | "transferOwnership"
  | "delete"
  | "resetMfa";

interface UsersProps {
  user: UserClass;
  cognitologin: CognitoLoginClass;
  allUsers: UserClass[];
  listOfWebapps: WebApplicationClass[];
  changeUserToAdminCallback?: () => void;
  navigate: (path: string) => void;
}

export default function Users({
  user,
  cognitologin,
  allUsers,
  listOfWebapps,
  changeUserToAdminCallback,
}: UsersProps) {
  const [userList, setUserList] = useState<UserClass[]>(allUsers);

  // Sync userList when allUsers prop is populated after async load
  useEffect(() => {
    setUserList(allUsers);
  }, [allUsers]);

  // Clear auto-dismiss timer on unmount
  useEffect(() => {
    return () => {
      if (transferDismissTimer.current) clearTimeout(transferDismissTimer.current);
    };
  }, []);

  const [currentPage, setCurrentPage] = useState(0);
  const [showAddUser, setShowAddUser] = useState(false);
  const [transferNotice, setTransferNotice] = useState<string | null>(null);
  const transferDismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Manage modal state
  const [managingUser, setManagingUser] = useState<UserClass | null>(null);
  const [manageView, setManageView] = useState<ManageView>("overview");
  const [manageError, setManageError] = useState(EMPTY_STRING);
  const [transferAppsTargetEmail, setTransferAppsTargetEmail] = useState(EMPTY_STRING);
  const [transferAppsSelectedIds, setTransferAppsSelectedIds] = useState<Set<string>>(new Set());
  const [isResendingInvite, setIsResendingInvite] = useState(false);
  const manageDialogRef = useRef<HTMLDivElement>(null);

  // Whether MFA is live for this tenant, used to gate the "Reset MFA" action.
  // Proxy: if the current admin has TOTP enrolled, the pool enforces MFA — so
  // the action is meaningful. While MFA is switched off everyone reads "NOMFA"
  // and the action stays hidden.
  const [mfaActive, setMfaActive] = useState(false);
  useEffect(() => {
    let cancelled = false;
    cognitologin.getPreferredMfa().then((method) => {
      if (!cancelled) setMfaActive(method === "SOFTWARE_TOKEN_MFA");
    });
    return () => { cancelled = true; };
  }, [cognitologin]);

  const webappOps = useMemo(() => new WebAppOperationsClass(), []);

  const sortedUserList = useMemo(() => {
    return [...userList].sort((userA, userB) => {
      const roleDiff = getRoleSortPriority(userA.role) - getRoleSortPriority(userB.role);
      if (roleDiff !== 0) return roleDiff;
      const nameA = (userA.displayName || `${userA.given_name} ${userA.family_name}`).toLowerCase();
      const nameB = (userB.displayName || `${userB.given_name} ${userB.family_name}`).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [userList]);

  const totalPages = Math.ceil(sortedUserList.length / USERS_PER_PAGE);
  const startIndex = currentPage * USERS_PER_PAGE;
  const pageUsers = sortedUserList.slice(startIndex, startIndex + USERS_PER_PAGE);

  // Seat cap for the tenant's plan. seatLimit === null means UNLIMITED
  // (e.g. enterprise) — no cap is shown and Add User stays enabled. A
  // number is the max TOTAL users incl. the owner; atLimit is the gate
  // for the header copy and the disabled Add User button.
  const { usage: tenantUsage, hasLoaded: seatLimitHasLoaded } = useTenantUsage();
  const userCount = sortedUserList.length;
  const seatLimit = tenantUsage?.seatLimit ?? null;
  const atSeatLimit = seatLimit != null && userCount >= seatLimit;

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  // Announce a completed action. Every action on this page reports through
  // here, so none of them can quietly skip the acknowledgement — a role change
  // used to, and was the only one that finished in silence.
  const showActionNotice = useCallback((message: string) => {
    setTransferNotice(message);
    if (transferDismissTimer.current) clearTimeout(transferDismissTimer.current);
    transferDismissTimer.current = setTimeout(() => setTransferNotice(null), NOTICE_DISMISS_MS);
  }, []);

  const openManageModal = useCallback((targetUser: UserClass) => {
    setManagingUser(targetUser);
    setManageView("overview");
    setManageError(EMPTY_STRING);
    setTransferAppsTargetEmail(EMPTY_STRING);
    setTransferAppsSelectedIds(new Set());
  }, []);

  const closeManageModal = useCallback(() => {
    setManagingUser(null);
    setManageView("overview");
    setManageError(EMPTY_STRING);
    setTransferAppsTargetEmail(EMPTY_STRING);
    setTransferAppsSelectedIds(new Set());
  }, []);

  // Escape closes the Manage dialog; focus moves into it on open and returns to
  // the Manage button that opened it on close. Escape is a deliberate dismissal,
  // so unlike the backdrop it closes from any view.
  useModalAccessibility(!!managingUser, closeManageModal, manageDialogRef);

  const handleToggleTransferApp = useCallback((appId: string) => {
    setTransferAppsSelectedIds((previous) => {
      const updated = new Set(previous);
      if (updated.has(appId)) {
        updated.delete(appId);
      } else {
        updated.add(appId);
      }
      return updated;
    });
  }, []);

  const handleToggleAllTransferApps = useCallback((ownedApps: WebApplicationClass[]) => {
    setTransferAppsSelectedIds((previous) => {
      const allSelected = ownedApps.every((webapp) => previous.has(webapp.id));
      if (allSelected) {
        return new Set();
      }
      return new Set(ownedApps.map((webapp) => webapp.id));
    });
  }, []);

  const isOwner = user.role.includes(CLIENT_OWNER_GROUP);

  // ----------------------------------------
  //   Manage actions
  // ----------------------------------------

  const handleChangeRole = useCallback(async (newRole: string) => {
    if (!managingUser) return;
    setManageError(EMPTY_STRING);

    if (managingUser.role.includes(CLIENT_OWNER_GROUP)) {
      setManageError(CANT_CHANGE_CLIENT_OWNER_ROLE_ERROR);
      return;
    }

    // Await the backend mutation and only reflect the change on success. Without
    // this, a rejected change (e.g. the resolver denies it) still showed as
    // applied until the next reload — the UI silently diverged from server state.
    const roleChanged = await managingUser.changeRole(newRole);
    if (!roleChanged) {
      setManageError(COULD_NOT_CHANGE_ROLE_ERROR);
      return;
    }
    setUserList((previous) => [...previous]);
    setManageView("overview");

    const changedName = managingUser.displayName || `${managingUser.given_name} ${managingUser.family_name}`;
    const newRoleLabel = newRole === ADMIN_GROUP ? ADMIN_DISPLAY_VALUE : PENTESTER_DISPLAY_VALUE;
    showActionNotice(`${changedName} is now ${newRoleLabel}.`);
  }, [managingUser, showActionNotice]);

  // Account ownership is the single most consequential action on this page: it
  // grants Billing (spending money, and cancelling the subscription) to the
  // target, DEMOTES the acting owner to Sys Admin, and cannot be undone by the
  // person doing it — only the new owner can hand it back. It is therefore
  // reached through a confirmation view that spells all of that out, rather
  // than firing on the first click like it used to.
  const handleConfirmTransferOwnership = useCallback(async () => {
    if (!managingUser) return;
    setManageError(EMPTY_STRING);

    if (!isOwner) {
      setManageError(CANT_CHANGE_CLIENT_OWNER_ROLE_ERROR);
      return;
    }
    if (managingUser.emailaddress === user.emailaddress) {
      setManageError(CANT_TRANSFER_OWNERSHIP_TO_SELF_ERROR);
      return;
    }

    // Wrapped: the mutation rejects on an unauthorized response, which
    // previously escaped as an unhandled rejection and left the dialog sitting
    // there with no error and nothing changed.
    try {
      const ownershipChanged = await managingUser.changeRoleToClientOwner();
      if (!ownershipChanged) {
        setManageError(COULD_NOT_TRANSFER_OWNERSHIP_ERROR);
        return;
      }
      changeUserToAdminCallback?.();
      setUserList((previous) => [...previous]);
      closeManageModal();
    } catch (transferOwnershipError) {
      setManageError(COULD_NOT_TRANSFER_OWNERSHIP_ERROR);
    }
  }, [managingUser, isOwner, user.emailaddress, changeUserToAdminCallback, closeManageModal]);

  const handleConfirmTransferApps = useCallback(async () => {
    if (!managingUser || !transferAppsTargetEmail) return;
    setManageError(EMPTY_STRING);

    if (transferAppsTargetEmail === managingUser.emailaddress) {
      setManageError(CANT_TRANSFER_APPS_TO_SELF_ERROR);
      return;
    }

    if (transferAppsSelectedIds.size === 0) {
      setManageError(NO_APPS_TO_TRANSFER_ERROR);
      return;
    }

    const appsToTransfer = listOfWebapps.filter((webapp) => transferAppsSelectedIds.has(webapp.id));

    const recipientUser = userList.find((u) => u.emailaddress === transferAppsTargetEmail);
    const recipientName = recipientUser
      ? (recipientUser.displayName || `${recipientUser.given_name} ${recipientUser.family_name}`)
      : transferAppsTargetEmail;

    // Awaited, and the notice reports what actually moved. These calls used to
    // be fire-and-forget, so the user was told "3 apps transferred" before any
    // of them had resolved — including when all three failed.
    const transferResults = await Promise.all(
      appsToTransfer.map(async (webapp) => ({
        webapp,
        moved: await webapp.changeOwner(transferAppsTargetEmail),
      }))
    );
    const movedApps = transferResults.filter((result) => result.moved).map((result) => result.webapp);

    if (movedApps.length === 0) {
      setManageError(COULD_NOT_TRANSFER_APPS_ERROR);
      return;
    }

    const appLabel = movedApps.map((webapp) => webapp.name).join(", ");
    const failedCount = appsToTransfer.length - movedApps.length;
    const notice =
      `${movedApps.length} app${movedApps.length !== 1 ? "s" : ""} transferred to ${recipientName}: ${appLabel}` +
      (failedCount > 0 ? `. ${failedCount} could not be transferred.` : "");
    showActionNotice(notice);

    closeManageModal();
  }, [managingUser, transferAppsTargetEmail, transferAppsSelectedIds, listOfWebapps, userList, closeManageModal, showActionNotice]);

  // Re-send a Cognito invite to a user who never accepted theirs. The backend
  // enforces the same cooldown as the self-service path, so a repeated click
  // cannot flood the invitee's inbox.
  const handleResendInvite = useCallback(async () => {
    if (!managingUser) return;
    setManageError(EMPTY_STRING);
    setIsResendingInvite(true);

    const result = await resendUserInvite(managingUser.emailaddress);
    setIsResendingInvite(false);

    if (!result.ok) {
      setManageError(resendInviteMessage(result.reason));
      return;
    }

    const invitedName = managingUser.displayName || `${managingUser.given_name} ${managingUser.family_name}`;
    showActionNotice(`Invite resent to ${invitedName} at ${managingUser.emailaddress}.`);
    closeManageModal();
  }, [managingUser, closeManageModal, showActionNotice]);

  const handleConfirmDelete = useCallback(async () => {
    if (!managingUser) return;
    setManageError(EMPTY_STRING);

    if (managingUser.emailaddress === user.emailaddress) {
      setManageError(CANT_DELETE_SELF_ERROR);
      return;
    }
    if (managingUser.role.includes(CLIENT_OWNER_GROUP)) {
      setManageError(CANT_DELETE_CLIENT_OWNER_ERROR);
      return;
    }

    // The BACKEND reassigns this user's apps to the caller as part of
    // delete_user; we only note the names here so the notice can list them.
    // Writing webapp.owner locally as well used to duplicate that, and wrote a
    // different value than the server does (email here, Cognito username
    // there), so the two could disagree until the next reload.
    const transferredAppNames = listOfWebapps
      .filter((webapp) => webapp.owner === managingUser.emailaddress)
      .map((webapp) => webapp.name);

    await managingUser.delete();

    const deletedName = managingUser.displayName || `${managingUser.given_name} ${managingUser.family_name}`;
    setUserList((previous) => previous.filter((u) => u !== managingUser));
    closeManageModal();

    if (transferredAppNames.length > 0) {
      const notice = `${deletedName} deleted. ${transferredAppNames.length} app${transferredAppNames.length !== 1 ? "s" : ""} transferred to you: ${transferredAppNames.join(", ")}`;
      showActionNotice(notice);
    }
  }, [managingUser, user, listOfWebapps, closeManageModal, showActionNotice]);

  const handleConfirmResetMfa = useCallback(async () => {
    if (!managingUser) return;
    setManageError(EMPTY_STRING);

    // Mirror the backend guards so the UI never issues a call the resolver will
    // reject: the Owner is NIMIS-support-only, and self uses the profile flow.
    if (managingUser.role.includes(CLIENT_OWNER_GROUP)) {
      setManageError(CANT_CHANGE_CLIENT_OWNER_ROLE_ERROR);
      return;
    }
    if (managingUser.emailaddress === user.emailaddress) {
      setManageError(CANT_DELETE_SELF_ERROR);
      return;
    }

    const wasReset = await managingUser.resetMfa();
    if (!wasReset) {
      setManageError(COULD_NOT_RESET_MFA_ERROR);
      return;
    }

    const resetName = managingUser.displayName || `${managingUser.given_name} ${managingUser.family_name}`;
    showActionNotice(`Two-factor authentication reset for ${resetName}. They'll set up a new authenticator at next login.`);
    closeManageModal();
  }, [managingUser, user.emailaddress, closeManageModal, showActionNotice]);

  // ----------------------------------------
  //   Manage modal
  // ----------------------------------------

  function renderManageModal(): React.ReactElement | null {
    if (!managingUser) return null;

    const targetRoleBadge = getRoleBadge(managingUser.role);
    const isTargetOwner = managingUser.role.includes(CLIENT_OWNER_GROUP);
    const isTargetSelf = managingUser.emailaddress === user.emailaddress;
    const isTargetInvitePending = managingUser.status === COGNITO_STATUS_INVITE_PENDING;
    const ownedApps = listOfWebapps.filter((webapp) => webapp.owner === managingUser.emailaddress);
    const otherUsers = userList.filter((u) => u.emailaddress !== managingUser.emailaddress);
    // Options for the Transfer-to dropdown. Label format matches the
    // prior <option> children: "Display Name (email@example.com)",
    // falling back to "Given Family" when displayName is absent.
    const transferToOptions = otherUsers.map((otherUser) => ({
      value: otherUser.emailaddress,
      label: `${otherUser.displayName || `${otherUser.given_name} ${otherUser.family_name}`} (${otherUser.emailaddress})`,
    }));

    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* A click outside is the one dismissal that is routinely accidental,
            so it is ignored on any view other than the overview — those are
            confirmations and multi-step selections, where losing the dialog
            mid-way is the wrong outcome. X and Cancel always close. */}
        <div
          className="absolute inset-0 bg-black/50"
          onClick={() => { if (manageView === "overview") closeManageModal(); }}
          aria-hidden="true"
        />
        <div
          ref={manageDialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="manage-user-title"
          className="relative z-10 w-full max-w-md rounded-xl bg-white shadow-xl border border-gray-200 flex flex-col max-h-[90vh]"
        >

          {/* Modal header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
            {manageView !== "overview" ? (
              <button
                type="button"
                onClick={() => { setManageView("overview"); setManageError(EMPTY_STRING); setTransferAppsTargetEmail(EMPTY_STRING); setTransferAppsSelectedIds(new Set()); }}
                className="text-sm text-gray-500 hover:text-gray-800"
              >
                ← Back
              </button>
            ) : (
              <div className="flex items-center gap-3">
                <UserAvatar
                  avatarUrl={managingUser.avatar}
                  alt=""
                  className="h-8 w-8 rounded-full object-cover ring-1 ring-gray-200 shrink-0"
                />
                <div className="min-w-0">
                  <p id="manage-user-title" className="text-sm font-semibold text-gray-900 truncate">
                    {managingUser.displayName || `${managingUser.given_name} ${managingUser.family_name}`}
                  </p>
                  <p className="text-xs text-gray-400 truncate">{managingUser.emailaddress}</p>
                </div>
              </div>
            )}
            <button type="button" onClick={closeManageModal} className="text-gray-400 hover:text-gray-600 shrink-0">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Error banner */}
          {manageError && (
            <div className="mx-6 mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 shrink-0">
              {manageError}
            </div>
          )}

          {/* Modal body */}
          <div className="overflow-y-auto p-6 flex flex-col gap-3">

            {/* ---- Overview ---- */}
            {manageView === "overview" && (
              <>
                {/* Role badge */}
                <div className="flex items-center justify-between px-4 py-3 rounded-lg bg-gray-50 border border-gray-100">
                  <span className="text-sm text-gray-600">Role</span>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${targetRoleBadge.className}`}>
                    {targetRoleBadge.label}
                  </span>
                </div>

                {/* Owned apps */}
                <div className="px-4 py-3 rounded-lg bg-gray-50 border border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Applications</span>
                    <span className="text-sm text-gray-900 font-medium">
                      {ownedApps.length > 0 ? `${ownedApps.length} owned` : "None"}
                    </span>
                  </div>
                  {ownedApps.length > 0 && (
                    <p className="text-xs text-gray-400 mt-1 truncate">
                      {ownedApps.map((webapp) => webapp.name).join(", ")}
                    </p>
                  )}
                </div>

                {/* Actions */}
                {!isTargetSelf && (
                  <>
                    <div className="border-t border-gray-100 pt-1" />

                    <button
                      type="button"
                      onClick={() => { setManageView("changeRole"); setManageError(EMPTY_STRING); }}
                      disabled={isTargetOwner}
                      className="flex w-full items-center gap-3 px-4 py-3 rounded-lg text-left text-sm text-gray-700 hover:bg-gray-50 border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Shield className="h-4 w-4 text-gray-400 shrink-0" />
                      <span>Change role</span>
                    </button>

                    {isOwner && (
                      <button
                        type="button"
                        onClick={() => { setManageView("transferOwnership"); setManageError(EMPTY_STRING); }}
                        disabled={isTargetOwner}
                        className="flex w-full items-center gap-3 px-4 py-3 rounded-lg text-left text-sm text-gray-700 hover:bg-gray-50 border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ArrowRightLeft className="h-4 w-4 text-gray-400 shrink-0" />
                        <span>Transfer account ownership</span>
                      </button>
                    )}

                    {/* Only offered while the invite is outstanding. Once the
                        user has signed in there is nothing to resend, and the
                        backend refuses it anyway. */}
                    {isTargetInvitePending && (
                      <button
                        type="button"
                        onClick={handleResendInvite}
                        disabled={isResendingInvite}
                        className="flex w-full items-center gap-3 px-4 py-3 rounded-lg text-left text-sm text-gray-700 hover:bg-gray-50 border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <MailPlus className="h-4 w-4 text-gray-400 shrink-0" />
                        <span>{isResendingInvite ? "Resending invite…" : "Resend invite"}</span>
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => { setManageView("transferApps"); setManageError(EMPTY_STRING); setTransferAppsTargetEmail(EMPTY_STRING); setTransferAppsSelectedIds(new Set()); }}
                      className="flex w-full items-center gap-3 px-4 py-3 rounded-lg text-left text-sm text-gray-700 hover:bg-gray-50 border border-gray-200"
                    >
                      <ArrowRightLeft className="h-4 w-4 text-gray-400 shrink-0" />
                      <span>Transfer web apps</span>
                    </button>

                    {/* Reset MFA — only when the tenant runs MFA, and never for
                        the Owner (NIMIS-support-only recovery). Lets an admin
                        recover a user who lost their authenticator device. */}
                    {mfaActive && (
                      <button
                        type="button"
                        onClick={() => { setManageView("resetMfa"); setManageError(EMPTY_STRING); }}
                        disabled={isTargetOwner}
                        className="flex w-full items-center gap-3 px-4 py-3 rounded-lg text-left text-sm text-gray-700 hover:bg-gray-50 border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ShieldOff className="h-4 w-4 text-gray-400 shrink-0" />
                        <span>Reset two-factor auth</span>
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => { setManageView("delete"); setManageError(EMPTY_STRING); }}
                      disabled={isTargetOwner}
                      className="flex w-full items-center gap-3 px-4 py-3 rounded-lg text-left text-sm text-red-600 hover:bg-red-50 border border-red-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="h-4 w-4 shrink-0" />
                      <span>Delete user</span>
                    </button>
                  </>
                )}
              </>
            )}

            {/* ---- Change role ----
                The role they hold now is marked and not clickable. Without it
                this view showed two unlabelled choices with no indication of
                where the user is starting from, so you could re-apply the role
                they already had — a no-op that spent a call and reported
                nothing, which reads exactly like a failure. Knowing what you
                are changing FROM is the useful thing at the moment of choosing.

                No confirmation step, deliberately: a role change is reversible
                in two clicks by the same person, the Account Owner can always
                restore it, and each option states what it grants right here,
                which is more use before the decision than a dialog after it. */}
            {manageView === "changeRole" && (
              <>
                <p className="text-sm text-gray-500">Select the new role for this user.</p>
                {ROLE_CHOICES.map((roleChoice) => {
                  const isCurrentRole = managingUser.role.includes(roleChoice.group);
                  return (
                    <button
                      key={roleChoice.group}
                      type="button"
                      onClick={() => handleChangeRole(roleChoice.group)}
                      disabled={isCurrentRole}
                      aria-current={isCurrentRole ? "true" : undefined}
                      className={[
                        "flex w-full items-center gap-3 px-4 py-3 rounded-lg text-left text-sm border",
                        isCurrentRole
                          ? "border-gray-200 bg-gray-50 text-gray-500 cursor-default"
                          : "border-gray-200 text-gray-700 hover:bg-gray-50",
                      ].join(" ")}
                    >
                      <Shield className={`h-4 w-4 shrink-0 ${roleChoice.iconClassName}`} />
                      <div className="min-w-0">
                        <p className="font-medium flex items-center gap-2">
                          {roleChoice.label}
                          {isCurrentRole && (
                            <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                              Current
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-400">{roleChoice.description}</p>
                      </div>
                    </button>
                  );
                })}
              </>
            )}

            {/* ---- Transfer web apps ---- */}
            {manageView === "transferApps" && (
              <>
                <p className="text-sm text-gray-500">
                  Select the apps to transfer, then choose the recipient.
                </p>

                {ownedApps.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No apps owned by this user.</p>
                ) : (
                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                    {/* Select all row */}
                    <label className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100">
                      <input
                        type="checkbox"
                        checked={ownedApps.every((webapp) => transferAppsSelectedIds.has(webapp.id))}
                        onChange={() => handleToggleAllTransferApps(ownedApps)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 shrink-0"
                      />
                      <span className="text-xs font-medium text-gray-600">
                        Select all ({ownedApps.length})
                      </span>
                    </label>
                    {/* Individual app rows */}
                    {ownedApps.map((webapp) => (
                      <label
                        key={webapp.id}
                        className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={transferAppsSelectedIds.has(webapp.id)}
                          onChange={() => handleToggleTransferApp(webapp.id)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 shrink-0"
                        />
                        <span className="text-sm text-gray-700">{webapp.name}</span>
                      </label>
                    ))}
                  </div>
                )}

                {/* fullWidth: the option labels carry an email address, so the
                    default "size to the widest label" behaviour pushed the
                    trigger past the dialog and put a scrollbar on it. */}
                <div className="flex min-w-0 flex-col gap-1.5">
                  <label className="text-xs font-medium text-gray-600">Transfer to</label>
                  <SortSelect
                    value={transferAppsTargetEmail}
                    onChange={setTransferAppsTargetEmail}
                    options={transferToOptions}
                    ariaLabel="Select a user…"
                    icon={ArrowRightLeft}
                    fullWidth
                  />
                </div>
              </>
            )}

            {/* ---- Transfer account ownership confirmation ---- */}
            {manageView === "transferOwnership" && (
              <>
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-amber-100 p-2 shrink-0">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      Make this person the Account Owner?
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">You cannot undo this yourself.</p>
                  </div>
                </div>
                {/* Every consequence stated, because none of them are obvious
                    from the button and all three are irreversible by the actor. */}
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                  <ul className="flex list-disc flex-col gap-1.5 pl-4">
                    <li>
                      <span className="font-medium">{managingUser.displayName || `${managingUser.given_name} ${managingUser.family_name}`}</span>{" "}
                      becomes the Account Owner, with access to billing, payment
                      details and the subscription.
                    </li>
                    <li>
                      You are demoted to {ADMIN_DISPLAY_VALUE} and lose billing access.
                    </li>
                    <li>
                      Only the new owner can transfer it back.
                    </li>
                  </ul>
                </div>
                {/* Naming the current role matters when it is Pentester: the
                    jump is larger than the button suggests. */}
                <p className="text-xs text-gray-500">
                  Their current role is {targetRoleBadge.label}.
                </p>
              </>
            )}

            {/* ---- Delete confirmation ---- */}
            {manageView === "delete" && (
              <>
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-red-100 p-2 shrink-0">
                    <Trash2 className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Delete this user?</p>
                    <p className="text-xs text-gray-500 mt-0.5">This action cannot be undone.</p>
                  </div>
                </div>
                {ownedApps.length > 0 && (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
                    {ownedApps.length} app{ownedApps.length !== 1 ? "s" : ""} owned by this user will be transferred to you:{" "}
                    <span className="font-medium">{ownedApps.map((webapp) => webapp.name).join(", ")}</span>
                  </div>
                )}
              </>
            )}

            {/* ---- Reset MFA confirmation ---- */}
            {manageView === "resetMfa" && (
              <>
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-blue-100 p-2 shrink-0">
                    <ShieldOff className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Reset two-factor authentication?</p>
                    <p className="text-xs text-gray-500 mt-0.5">Use this when the user has lost their authenticator device.</p>
                  </div>
                </div>
                <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-600">
                  Their current authenticator stops working and they'll be prompted to set up a new one the next time they sign in. Their password is unchanged.
                </div>
              </>
            )}
          </div>

          {/* Modal footer */}
          {(manageView === "transferApps" ||
            manageView === "transferOwnership" ||
            manageView === "delete" ||
            manageView === "resetMfa") && (
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setManageView("overview"); setManageError(EMPTY_STRING); }}
              >
                Cancel
              </Button>
              {manageView === "transferApps" && (
                <Button
                  size="sm"
                  onClick={handleConfirmTransferApps}
                  disabled={transferAppsSelectedIds.size === 0 || !transferAppsTargetEmail}
                  className="bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50"
                >
                  Transfer apps
                </Button>
              )}
              {manageView === "transferOwnership" && (
                <Button
                  size="sm"
                  onClick={handleConfirmTransferOwnership}
                  className="bg-amber-600 hover:bg-amber-500 text-white"
                >
                  Transfer ownership
                </Button>
              )}
              {manageView === "delete" && (
                <Button
                  size="sm"
                  onClick={handleConfirmDelete}
                  className="bg-red-600 hover:bg-red-500 text-white"
                >
                  Delete user
                </Button>
              )}
              {manageView === "resetMfa" && (
                <Button
                  size="sm"
                  onClick={handleConfirmResetMfa}
                  className="bg-blue-600 hover:bg-blue-500 text-white"
                >
                  Reset two-factor
                </Button>
              )}
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  }

  // ----------------------------------------
  //   Render
  // ----------------------------------------

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-50">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="inline-flex items-center gap-1.5">
              <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
              <HelpTip label="What each role can do" content={<RolesLegend />} maxWidthPx={280} />
            </span>
            {/* Three states, not two. A null seatLimit means UNLIMITED, but it
                also reads as null while the usage read is still in flight —
                printing the unlimited phrasing in that window is what made the
                count visibly correct itself from "1 user in your organization"
                to "1 of 5 users" a moment later. Until the read has landed,
                state only the part we already know. */}
            <p className="mt-1 text-sm text-gray-500">
              {!seatLimitHasLoaded
                ? `${userCount} user${userCount !== 1 ? "s" : ""}`
                : seatLimit != null
                ? `${userCount} of ${seatLimit} user${seatLimit !== 1 ? "s" : ""}${
                    atSeatLimit ? " — limit reached" : ""
                  }`
                : `${userCount} user${userCount !== 1 ? "s" : ""} in your organization`}
            </p>
          </div>
          {/* Matches the Test button on the WebApplication page —
              outline pill, blue border + blue text on white. Keeps the
              primary affordance consistent across pages without the
              visual weight of a solid blue fill. */}
          <button
            type="button"
            onClick={() => setShowAddUser(true)}
            disabled={atSeatLimit}
            title={atSeatLimit ? SEAT_LIMIT_REACHED_TOOLTIP : undefined}
            className="inline-flex items-center justify-center gap-1.5 rounded-full border border-blue-500 px-4 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 hover:border-blue-600 transition-colors duration-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-blue-500"
          >
            <UserPlus className="h-3.5 w-3.5" />
            Add user
          </button>
        </div>

        {/* Transfer notice */}
        {transferNotice && (
          <div className="mb-4 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 flex items-start gap-3">
            <ArrowRightLeft className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
            <p className="text-sm text-blue-700 flex-1">{transferNotice}</p>
            <button
              type="button"
              onClick={() => setTransferNotice(null)}
              className="text-blue-400 hover:text-blue-600 shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* User list card */}
        <Card>
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">All users</h2>
            <p className="text-xs text-gray-400 mt-0.5">Click Manage to change a role, resend an invite, transfer apps or ownership, or delete a user</p>
          </div>

          <CardContent className="p-0">
            {pageUsers.length === 0 ? (
              <div className="text-center py-12">
                <UsersIcon className="mx-auto h-10 w-10 text-gray-300" />
                <p className="mt-3 text-sm text-gray-500">No users found</p>
              </div>
            ) : (
              <div className="flex flex-col gap-4 p-4">
                {pageUsers.map((rowUser, pageIndex) => {
                  const globalIndex = startIndex + pageIndex;
                  const roleBadge = getRoleBadge(rowUser.role);
                  const ownedAppNames = webappOps.getAppNamesByOwnerUsername(listOfWebapps, rowUser.emailaddress);

                  return (
                    <div
                      key={globalIndex}
                      className="flex items-start gap-5 px-6 py-5 rounded-xl bg-white border border-gray-200 shadow-sm transition-shadow hover:shadow-md"
                    >
                      <UserAvatar
                        avatarUrl={rowUser.avatar}
                        alt={rowUser.displayName}
                        className="h-10 w-10 rounded-full object-cover ring-1 ring-gray-200 shrink-0 mt-0.5"
                      />

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-base font-semibold text-gray-900 truncate">
                            {rowUser.displayName || `${rowUser.given_name} ${rowUser.family_name}`}
                          </span>
                          <span className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${roleBadge.className}`}>
                            {roleBadge.label}
                          </span>
                          {/* A user who has been invited but has never signed
                              in. Without this the list gave no way to tell an
                              active colleague from an invite nobody acted on. */}
                          {rowUser.status === COGNITO_STATUS_INVITE_PENDING && (
                            <span className="shrink-0 inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20">
                              Invite pending
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-gray-500 truncate">{rowUser.emailaddress}</p>
                      </div>

                      {/* Owned apps. Both states render the SAME two-line shape
                          so the column keeps one baseline down the list: with no
                          apps the count line simply reads "No apps" and the names
                          line is empty. Previously the empty state collapsed to a
                          single line and floated against the two-line name block
                          beside it. Narrower, too — 320px was reserved for what
                          is usually a short list or nothing at all. */}
                      <div className="text-right w-56 min-w-0 max-lg:!hidden flex flex-col self-center">
                        <span className="text-sm text-gray-500">
                          {ownedAppNames.length > 0
                            ? `${ownedAppNames.length} app${ownedAppNames.length !== 1 ? "s" : ""}`
                            : "No apps"}
                        </span>
                        <span className="mt-1 text-sm text-gray-400 truncate w-full">
                          {ownedAppNames.join(", ")}
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() => openManageModal(rowUser)}
                        className="self-center inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer shrink-0"
                      >
                        Manage
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                Showing {startIndex + 1}–{Math.min(startIndex + USERS_PER_PAGE, sortedUserList.length)} of {sortedUserList.length}
              </p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {Array.from({ length: totalPages }, (_, pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    onClick={() => handlePageChange(pageNumber)}
                    className={[
                      "rounded-lg px-3 py-1 text-sm transition-colors",
                      currentPage === pageNumber
                        ? "bg-blue-600 text-white"
                        : "text-gray-500 hover:bg-gray-100",
                    ].join(" ")}
                  >
                    {pageNumber + 1}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages - 1}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </Card>
      </div>

      <AddUser show={showAddUser} close={() => setShowAddUser(false)} />

      {renderManageModal()}
    </div>
  );
}
